import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Wine, AlertTriangle, Info } from 'lucide-react';
import { useOrderHistory } from '../../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../../stores/appRestaurantStore';
import { useAppUserDefaultStore } from '../../../../stores/appUserDefaultStore';
import { useInventoryStore } from '../../../../stores/appInventoryStore';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadCostPrice,
  useLoadSubrecipes,
  useLoadInventoryCountsHistory,
} from '../../../../utils/dataLoadingUtils';
import { resolveWastePercentage } from '../../../../utils/wasteUtils';
import { eur } from '../../../../components/orderhistory/utils';
import { fmtBaseOrDash } from '../../../../utils/reports/shared';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import { SectionCard, EmptyState, EmptyCard, Kpi, KpiGrid, PillToggle, ReportSelect, ReportLoading, ReportTable, ReportColumn, ReportBarChart, CHART_RED } from './common';
import {
  buildConsumptionWindows,
  computeConsumptionReport,
  ConsumptionRow,
  ConsumptionCustomRow,
} from '../../../../utils/reports/consumptionReport';

const ConsumptionReport: React.FC = () => {
  const { t } = useTranslation();

  // Data is scoped to the selected restaurant by these hooks.
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();
  useLoadInventoryCountsHistory();

  const orders = useOrderHistory((s) => s.orderHistoryList);
  const revenue = useRevenueHistory((s) => s.revenueHistoryList);
  const costPriceList = useCostPriceStore((s) => s.costPriceList);
  const subrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const countsHistory = useInventoryStore((s) => s.countsHistory);
  const isLoadingCounts = useInventoryStore((s) => s.isLoadingCounts);
  const restaurantId = useAppUserDefaultStore((s) => s.restaurant?.id) ?? '';
  const reportSettings = useRestaurantStore((s) => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const defaultWastePercentage = useMemo(
    () => resolveWastePercentage(reportSettings?.wastePercentage),
    [reportSettings]
  );

  // Shares the 'consumption.windows' cache with the hub so the windows build runs
  // once and is reused when navigating between the two.
  const windows = cachedReportValue('consumption.windows', [countsHistory], () =>
    buildConsumptionWindows(countsHistory)
  );

  // --- Window selection state ---
  const [scope, setScope] = useState<'group' | 'section'>('group');
  const [selectionId, setSelectionId] = useState<string>('');
  const [startKey, setStartKey] = useState<string>('');
  const [endKey, setEndKey] = useState<string>('');

  // The number of options for the currently-selected scope.
  const scopeOptionCount = scope === 'group' ? windows.groups.length : windows.sections.length;
  const currentOption =
    scope === 'group'
      ? windows.groups.find((g) => g.groupKey === selectionId)
      : windows.sections.find((s) => s.sectionId === selectionId);

  /** Select a scope's first option + its two most recent points (start = older). */
  const applyDefaults = (nextScope: 'group' | 'section') => {
    setScope(nextScope);
    if (nextScope === 'group') {
      const first = windows.groups[0];
      setSelectionId(first?.groupKey ?? '');
      setEndKey(first?.points[0].key ?? '');
      setStartKey(first?.points[1].key ?? '');
    } else {
      const first = windows.sections[0];
      setSelectionId(first?.sectionId ?? '');
      setEndKey(first?.points[0].key ?? '');
      setStartKey(first?.points[1].key ?? '');
    }
  };

  // Pick sensible defaults whenever the available windows change.
  useEffect(() => {
    applyDefaults(windows.groups.length > 0 ? 'group' : 'section');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windows]);

  const data = cachedReportValue(
    'consumption.data',
    [countsHistory, orders, revenue, costPriceList, subrecipesById, defaultWastePercentage, restaurantId, scope, selectionId, startKey, endKey],
    () =>
      computeConsumptionReport({
        countsHistory,
        orders,
        revenue,
        costPriceList,
        subrecipesById,
        defaultWastePercentage,
        currentRestaurantId: restaurantId,
        scope,
        selectionId,
        startPointKey: startKey,
        endPointKey: endKey,
      })
  );

  // While counts are still loading and empty, show a spinner instead of flashing
  // the "no comparable counts" empty state.
  if (isLoadingCounts && countsHistory.length === 0) {
    return <ReportLoading />;
  }

  // No comparable counts at all (also gated by the registry, but guard direct URLs).
  if (!windows.hasAnyWindow) {
    return <EmptyCard message={t('reports.consumption.empty')} icon={Wine} />;
  }

  const onSelectionChange = (id: string) => {
    setSelectionId(id);
    const opt =
      scope === 'group'
        ? windows.groups.find((o) => o.groupKey === id)
        : windows.sections.find((o) => o.sectionId === id);
    if (opt) {
      setEndKey(opt.points[0].key);
      setStartKey(opt.points[1].key);
    }
  };

  return (
    <div className="mt-4">
      {/* Window selector — count-based, ignores the page time filter */}
      <SectionCard
        title={t('reports.consumption.window.title')}
        subtitle={t('reports.consumption.window.subtitle')}
      >
        <div className="flex flex-wrap gap-3 items-center">
          {/* Group / section toggle */}
          <PillToggle
            options={[
              { value: 'group', label: t('reports.consumption.scope.group') },
              { value: 'section', label: t('reports.consumption.scope.section') },
            ]}
            value={scope}
            onChange={applyDefaults}
            isDisabled={(s) => (s === 'group' ? windows.groups : windows.sections).length === 0}
          />

          {/* Group / section picker */}
          <ReportSelect value={selectionId} onChange={(e) => onSelectionChange(e.target.value)}>
            {scope === 'group'
              ? windows.groups.map((g) => (
                  <option key={g.groupKey} value={g.groupKey}>
                    {g.groupName || t('reports.consumption.ungrouped')}
                  </option>
                ))
              : windows.sections.map((s) => (
                  <option key={s.sectionId} value={s.sectionId}>
                    {s.groupName ? `${s.groupName} · ${s.sectionName}` : s.sectionName}
                  </option>
                ))}
          </ReportSelect>

          {/* Start / end count pickers */}
          <label className="flex items-center gap-2 text-sm text-muted dark:text-muted-dark">
            {t('reports.consumption.window.from')}
            <ReportSelect value={startKey} onChange={(e) => setStartKey(e.target.value)}>
              {currentOption?.points.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} · {eur(p.totalValue)}
                </option>
              ))}
            </ReportSelect>
          </label>
          <label className="flex items-center gap-2 text-sm text-muted dark:text-muted-dark">
            {t('reports.consumption.window.to')}
            <ReportSelect value={endKey} onChange={(e) => setEndKey(e.target.value)}>
              {currentOption?.points.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} · {eur(p.totalValue)}
                </option>
              ))}
            </ReportSelect>
          </label>
        </div>
        {scopeOptionCount === 0 && <EmptyState message={t('reports.consumption.window.noOptions')} />}
      </SectionCard>

      {!data.hasData || !data.window ? (
        <EmptyState message={t('reports.consumption.window.pickTwo')} />
      ) : (
        <>
          {/* Summary KPIs */}
          <KpiGrid>
            <Kpi
              label={t('reports.consumption.kpi.consumed')}
              value={eur(data.totalConsumedEur)}
              hint={t('reports.consumption.kpi.window', { start: data.window.startLabel, end: data.window.endLabel })}
            />
            <Kpi label={t('reports.consumption.kpi.trueWaste')} value={eur(data.totalTrueWasteEur)} tone={data.totalTrueWasteEur > 0 ? 'up' : 'neutral'} />
            <Kpi
              label={t('reports.consumption.kpi.stockDelta')}
              value={eur(data.stockDeltaEur)}
              hint={t('reports.consumption.kpi.stockDeltaHint')}
            />
          </KpiGrid>

          {/* Unmeasurable-purchases notice */}
          {data.unmeasurableProductNames.length > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {t('reports.consumption.unmeasurable', {
                  count: data.unmeasurableProductNames.length,
                  products: data.unmeasurableProductNames.join(', '),
                })}
              </p>
            </div>
          )}

          {/* Top true-waste products chart */}
          {(() => {
            const chartData = data.rows
              .filter((r) => r.trueWasteEur !== null && r.trueWasteEur > 0)
              .sort((a, b) => (b.trueWasteEur as number) - (a.trueWasteEur as number))
              .slice(0, 10)
              .map((r) => ({ label: r.productName, value: r.trueWasteEur as number }));
            return chartData.length > 0 ? (
              <SectionCard title={t('reports.consumption.chart.title')} subtitle={t('reports.consumption.chart.subtitle')}>
                <ReportBarChart data={chartData} valueFormatter={eur} defaultColor={CHART_RED} />
              </SectionCard>
            ) : null;
          })()}

          {/* Main table */}
          <SectionCard
            title={t('reports.consumption.table.title')}
            subtitle={t('reports.consumption.table.subtitle')}
          >
            {data.rows.length === 0 ? (
              <EmptyState message={t('reports.consumption.table.empty')} />
            ) : (
              <ConsumptionTable rows={data.rows} t={t} sectionScope={data.window.scope === 'section'} />
            )}
            <p className="text-xs text-muted dark:text-muted-dark mt-3">
              {t('reports.consumption.table.footnote')}
            </p>
          </SectionCard>

          {/* Custom items */}
          {data.customItems.length > 0 && (
            <SectionCard
              title={t('reports.consumption.custom.title')}
              subtitle={t('reports.consumption.custom.subtitle')}
            >
              <CustomTable rows={data.customItems} t={t} />
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
};

// --- sub-components --------------------------------------------------------

const ConsumptionTable: React.FC<{ rows: ConsumptionRow[]; t: TFunction; sectionScope: boolean }> = ({
  rows,
  t,
  sectionScope,
}) => {
  const columns: ReportColumn<ConsumptionRow>[] = [
    {
      key: 'product',
      header: t('reports.consumption.col.product'),
      render: (r) => (
        <>
          <span className="font-medium">{r.productName}</span>
          {r.negativeConsumption && (
            <AlertTriangle className="inline w-3.5 h-3.5 text-amber-600 dark:text-amber-400 ml-1 -mt-0.5" aria-label={t('reports.consumption.flag.negative')} />
          )}
          {sectionScope && r.crossSection && (
            <Info className="inline w-3.5 h-3.5 text-blue-500 dark:text-blue-400 ml-1 -mt-0.5" aria-label={t('reports.consumption.flag.crossSection')} />
          )}
          {/* The two counts were frozen in different unit spaces: rebased through the
              frozen pack size, or — when nothing could bridge them — not subtracted at all,
              which is why every quantity on the row reads "—". */}
          {r.countUnitMismatch && (
            <AlertTriangle
              className="inline w-3.5 h-3.5 text-amber-600 dark:text-amber-400 ml-1 -mt-0.5"
              aria-label={t(r.countQtyUnreadable ? 'reports.consumption.flag.countUnitUnreadable' : 'reports.consumption.flag.countUnit')}
            />
          )}
          {r.dishes.length > 0 && <p className="text-xs text-muted dark:text-muted-dark">{r.dishes.join(', ')}</p>}
        </>
      ),
    },
    { key: 'start', header: t('reports.consumption.col.start'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{fmtBaseOrDash(r.startQty, r.stdUnit)}</span> },
    { key: 'purchased', header: t('reports.consumption.col.purchased'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{r.hasUnmeasurablePurchases ? `${fmtBaseOrDash(r.purchasedQty, r.stdUnit)} *` : fmtBaseOrDash(r.purchasedQty, r.stdUnit)}</span> },
    { key: 'end', header: t('reports.consumption.col.end'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{fmtBaseOrDash(r.endQty, r.stdUnit)}</span> },
    { key: 'consumed', header: t('reports.consumption.col.consumed'), align: 'right', render: (r) => fmtBaseOrDash(r.consumedQty, r.stdUnit) },
    { key: 'needed', header: t('reports.consumption.col.needed'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{fmtBaseOrDash(r.neededQty, r.stdUnit)}</span> },
    { key: 'stockBuildUp', header: t('reports.consumption.col.stockBuildUp'), align: 'right', render: (r) => (r.stockBuildUpEur !== null ? eur(r.stockBuildUpEur) : '—') },
    {
      key: 'trueWaste',
      header: t('reports.consumption.col.trueWaste'),
      align: 'right',
      render: (r) => (r.trueWasteEur !== null ? eur(r.trueWasteEur) : t('reports.consumption.naValue')),
      cellClassName: (r) => `font-medium ${r.trueWasteEur !== null && r.trueWasteEur > 0 ? 'text-textRed dark:text-textRed-dark' : ''}`,
    },
  ];
  return (
    <ReportTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      rowClassName={(r) => (r.negativeConsumption ? 'bg-amber-50/60 dark:bg-amber-900/10' : '')}
    />
  );
};

const CustomTable: React.FC<{ rows: ConsumptionCustomRow[]; t: TFunction }> = ({ rows, t }) => {
  const columns: ReportColumn<ConsumptionCustomRow>[] = [
    { key: 'product', header: t('reports.consumption.col.product'), render: (r) => <span className="font-medium">{r.productName}</span> },
    { key: 'start', header: t('reports.consumption.col.start'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{fmtBaseOrDash(r.startQty, r.stdUnit)}</span> },
    { key: 'end', header: t('reports.consumption.col.end'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{fmtBaseOrDash(r.endQty, r.stdUnit)}</span> },
    { key: 'delta', header: t('reports.consumption.custom.delta'), align: 'right', render: (r) => <span className="font-medium">{r.deltaEur !== null ? eur(r.deltaEur) : fmtBaseOrDash(r.deltaQty, r.stdUnit)}</span> },
  ];
  return <ReportTable columns={columns} rows={rows} rowKey={(r) => r.key} />;
};

export default ConsumptionReport;
